import { enabledEventTypes, useMyAccessModes } from "@/features/clinical-access/api/selectMyAccessModes";
import type { EventTypeValue } from "@/features/clinical-access/types";
import { BulkUpdateEventsScreen } from "@/features/patients/features/overview/table/bulk/BulkUpdateEventsScreen";
import { patientsBulkEventBreadcrumbs } from "@/features/patients/features/overview/table/bulk/bulkNav";
import { parseBulkDiaryIdsSearch } from "@/features/patients/features/overview/table/bulk/bulkSearch";
import { useBulkDiaryIds } from "@/features/patients/features/overview/table/bulk/useBulkDiaryIds";
import { hooks as h } from "@/hooks";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

const eventTypeParamSchema = z.enum(["injection", "shockwave", "surgery", "education", "product", "medication"]);

function BulkEventsByTypeRoute() {
    const { eventType } = Route.useParams();
    const search = Route.useSearch();
    const diaryIds = useBulkDiaryIds(search.diaryIds ?? []);
    const { data: accessModes } = useMyAccessModes();
    const enabledTypes = useMemo(() => enabledEventTypes(accessModes), [accessModes]);

    h.useNav(
        diaryIds.length > 0
            ? patientsBulkEventBreadcrumbs({
                  eventType: eventType as EventTypeValue,
                  diaryIds,
                  search: search as Record<string, unknown>,
                  enabledEventTypes: enabledTypes,
              })
            : undefined
    );

    if (diaryIds.length === 0) return null;
    return <BulkUpdateEventsScreen diaryIds={diaryIds} initialEventType={eventType as EventTypeValue} />;
}

export const Route = createFileRoute("/_app/patients/bulk/events/$eventType")({
    params: {
        parse: (params) => ({
            eventType: eventTypeParamSchema.parse(params.eventType),
        }),
        stringify: ({ eventType }) => ({ eventType }),
    },
    validateSearch: (search: Record<string, unknown>) => parseBulkDiaryIdsSearch(search),
    component: BulkEventsByTypeRoute,
});
